<template>
  <app-card design="new" tag="article" class="release-preview-card">
    <template #header>
      <div class="release-preview-card__heading">
        <div class="release-preview-card__title-row">
          <h2>{{ release.title || 'Untitled release' }}</h2>
          <app-status-badge
            v-if="showStatus && release.status"
            design="new"
            :label="statusLabel"
            :tone="statusTone"
            compact
          />
        </div>
        <time v-if="dateLabel" class="release-preview-card__date">{{ dateLabel }}</time>
      </div>
    </template>
    <p v-if="release.summary" class="release-preview-card__summary">{{ release.summary }}</p>

    <ol v-if="orderedItems.length" class="release-preview-card__items">
      <li v-for="(item, index) in orderedItems" :key="item.id" class="release-preview-item">
        <span class="release-preview-item__number" aria-hidden="true">{{ index + 1 }}</span>
        <div class="release-preview-item__copy">
          <h3>{{ item.title }}</h3>
          <p v-if="item.summary">{{ item.summary }}</p>
          <div v-if="item.tags && item.tags.length" class="release-preview-item__tags">
            <app-status-badge
              v-for="tag in item.tags"
              :key="tag.id"
              design="new"
              :label="tag.label"
              :color="tag.color"
              compact
            />
          </div>
        </div>
      </li>
    </ol>
    <app-no-results
      v-else
      design="new"
      small
      title="No release items"
      text="Add separate release items to build the release notes."
    />
    <footer v-if="showDetail" class="release-preview-card__footer">
      <app-button design="new" theme="dark" size="small" @click.native="$emit('open-detail', release)">
        {{ detailLabel }}
      </app-button>
    </footer>
  </app-card>
</template>

<script>
import { format, isValid, parseISO } from 'date-fns'
import { enUS, ru } from 'date-fns/locale'
import { orderedItems } from './releaseState'

export default {
  name: 'TechSupportReleasePreview',
  props: {
    release: {
      type: Object,
      required: true
    },
    language: {
      type: String,
      default: 'en'
    },
    showDetail: {
      type: Boolean,
      default: false
    },
    showStatus: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dateLabel() {
      const value = this.release.release_date || this.release.published_at
      const parsed = value ? parseISO(value) : null
      if (!parsed || !isValid(parsed)) return ''
      const locale = this.language === 'ru' ? ru : enUS
      return format(parsed, 'dd MMMM yyyy', { locale })
    },
    detailLabel() {
      return this.language === 'ru' ? 'Открыть весь релиз' : 'View full release'
    },
    orderedItems() {
      return orderedItems(this.release.items)
    },
    statusLabel() {
      const value = this.release.status || ''
      return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : ''
    },
    statusTone() {
      return {
        draft: 'amber',
        published: 'green',
        archived: 'neutral'
      }[this.release.status] || 'neutral'
    }
  }
}
</script>

<style lang="scss" scoped>
.release-preview-card__title-row {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-8;
}

.release-preview-card__title-row h2 {
  margin: 0;
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: $ui-font-size-entity-title;
  font-weight: 600;
  line-height: 1.3;
}

.release-preview-card__date {
  display: block;
  margin-top: $ui-space-4;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}

.release-preview-card__summary,
.release-preview-item p {
  white-space: pre-line;
}

.release-preview-card__summary {
  max-width: 760px;
  margin: 0 0 $ui-space-16;
  color: $ui-text;
  line-height: 1.55;
}

.release-preview-card__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: $ui-space-16;
  padding-top: $ui-space-16;
  border-top: 1px solid $ui-line;
}

.release-preview-card__items {
  margin: 0;
  padding: 0;
  border-top: 1px solid $ui-line;
  list-style: none;
}

.release-preview-item {
  display: grid;
  min-width: 0;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: $ui-space-12;
  padding: $ui-space-16 0;
  border-bottom: 1px solid $ui-line;
}

.release-preview-item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.release-preview-item__number {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: $ui-control-radius;
  background-color: $ui-blue-soft;
  color: $ui-blue;
  font-family: $ui-font-heading;
  font-size: $ui-font-size-metadata;
  font-weight: 600;
}

.release-preview-item__copy {
  min-width: 0;
}

.release-preview-item h3 {
  margin: 2px 0 0;
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: $ui-font-size-body;
  font-weight: 600;
}

.release-preview-item p {
  margin: $ui-space-4 0 0;
  color: $ui-muted;
  line-height: 1.5;
}

.release-preview-item__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-8;
  margin-top: $ui-space-8;
}

@media (max-width: 760px) {
  .release-preview-card__summary { max-width: none; }
}
</style>
